import React from "react";
import clsx from "clsx";
import { Divider, Paper, Typography, Grid } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Theme } from "../.themes";
import InfoIcon from "@material-ui/icons/Info";
import SvgIcon from "@material-ui/core/SvgIcon";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "block",
    padding: theme.spacing(3),
    textAlign: theme.messageBox?.textAlign ?? "left",
    borderColor: theme.messageBox?.borderColor,
    backgroundColor: theme.messageBox?.backgroundColor,
    borderWidth: parseInt(theme.border),
    borderRadius: theme.messageBox?.borderRadius ?? 0,
  },
  primary: {
    borderColor: theme.palette.primary.main,
  },
  secondary: {
    borderColor: theme.palette.secondary.main,
  },
  header: {
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  content: {},
  divider: {
    backgroundColor: theme.messageBox?.dividerColor,
    margin: `${theme.spacing(2)}px 0`,
  },
  plain: {
    backgroundColor: "#00000000",
    border: "0",
    padding: `0 ${theme.spacing(3)}px`,
  },
}));

type propTypes = {
  /**
   * The content of the box.
   */
  header?: string;
  /**
   * The content of the box.
   */
  children?: string;
  /**
   * The border color of the component. It supports those theme colors that make sense for this component.
   */
  color?: "primary" | "secondary";
  /**
   * Message box styling - outlined with border or plain/transparent
   */
  variant?: "outlined" | "plain";
  /**
   * Icon override
   */
  icon?: React.ReactElement;
};

export const MessageBox = (props: propTypes) => {
  const theme: Theme = useTheme();
  const classes = useStyles(theme);

  if (!props.header && !props.children) {
    return null;
  }

  let color = undefined;

  if (props.color !== undefined) {
    color = [classes[props.color]];
  }

  const variant = props.variant === "plain" ? undefined : "outlined";

  const element = props.icon ?? (theme.icons?.infoIcon && <SvgIcon>{theme.icons?.infoIcon}</SvgIcon>) ?? <InfoIcon />;
  return (
    <Paper
      variant={variant}
      elevation={!variant ? 0 : undefined}
      square
      className={clsx(classes.root, color, !variant && classes.plain)}
      id='ws-messageBox'
    >
      <Grid container spacing={4} direction='row' wrap='nowrap'>
        <Grid item xs={1}>
          {element}
        </Grid>
        <Grid item xs={11}>
          {props.header && (
            <Typography variant='h6' className={classes.header}>
              {props.header}
            </Typography>
          )}
          {props.header && props.children && <Divider className={classes.divider} />}
          {props.children && <Typography className={classes.content}>{props.children}</Typography>}
        </Grid>
      </Grid>
    </Paper>
  );
};
